import { useState, useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import * as api from '../../api/adminApi';
import { PageHeader, StatCard, StatRow, ToggleGroup, cardStyle, sectionTitleStyle, LoadingState, ErrorState, Avatar, timeAgo } from './shared';

export default function DashboardPage() {
  const auth = useAuthContext();
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.getDashboardSummary>> | null>(null);
  const [growth, setGrowth] = useState<Awaited<ReturnType<typeof api.getUserGrowth>> | null>(null);
  const [distribution, setDistribution] = useState<Awaited<ReturnType<typeof api.getPlanDistribution>> | null>(null);
  const [activity, setActivity] = useState<Awaited<ReturnType<typeof api.getRecentActivity>> | null>(null);
  const [growthRange, setGrowthRange] = useState('30D');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, g, d, a] = await Promise.all([
        api.getDashboardSummary(),
        api.getUserGrowth(growthRange.toLowerCase()),
        api.getPlanDistribution(),
        api.getRecentActivity(),
      ]);
      setSummary(s); setGrowth(g); setDistribution(d); setActivity(a);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [growthRange]);

  if (loading && !summary) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={today}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Search users, docs..." readOnly
              style={{ padding: '8px 12px 8px 36px', fontSize: 13, borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', width: 180, fontFamily: '"Geist", system-ui, sans-serif', outline: 'none', cursor: 'pointer' }}
            />
            <svg style={{ position: 'absolute', left: 10, top: 9, color: '#8B7D6B' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <Avatar name={auth.user?.displayName ?? 'Admin'} size={36} />
        </div>
      </PageHeader>

      {/* KPI Cards */}
      <StatRow>
        <StatCard label="TOTAL USERS" value={summary?.totalUsers.toLocaleString() ?? '—'} accent={<span style={{ color: '#22883F', fontSize: 14, fontWeight: 500 }}>+12%</span>} sub="84 new this month" />
        <StatCard label="MONTHLY REVENUE" value={`$${summary?.monthlyRevenue.toLocaleString() ?? '0'}`} accent={<span style={{ color: '#22883F', fontSize: 14, fontWeight: 500 }}>+8%</span>} sub={`${distribution ? distribution.pro + ' Pro · ' + distribution.enterprise + ' Enterprise' : ''}`} />
        <StatCard label="ACTIVE TODAY" value={String(summary?.activeToday ?? 0)} accent={<span style={{ color: '#8B7D6B', fontSize: 14 }}>{summary?.totalUsers ? Math.round((summary.activeToday / summary.totalUsers) * 100) : 0}% of total</span>} sub="Peak: 2–4 PM EST" />
        <StatCard label="AI REQUESTS TODAY" value={summary?.aiRequestsToday.toLocaleString() ?? '0'} accent={<span style={{ color: '#D46A3A', fontSize: 14, fontWeight: 500 }}>${summary?.totalUsers ? ((summary.aiRequestsToday * 0.04) / 5).toFixed(2) : '0'}</span>} sub={`Avg ${summary?.totalUsers ? (summary.aiRequestsToday / Math.max(summary.activeToday, 1)).toFixed(1) : '0'} per active user`} />
      </StatRow>

      {/* User Growth + Plan Distribution */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {/* User Growth Chart */}
        <div style={{ ...cardStyle, flex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={sectionTitleStyle}>User Growth</span>
            <ToggleGroup options={['7D', '30D', '90D']} value={growthRange} onChange={setGrowthRange} />
          </div>
          {growth && growth.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 180 }}>
              {growth.map((d, i) => {
                const max = Math.max(...growth.map(g => g.count), 1);
                const h = Math.max((d.count / max) * 160, 4);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '80%', height: h, backgroundColor: '#D46A3A', borderRadius: 3, opacity: 0.6 + (i / growth.length) * 0.4 }} />
                    {(i === 0 || i === Math.floor(growth.length / 2) || i === growth.length - 1) && (
                      <span style={{ fontSize: 11, color: '#8B7D6B' }}>{new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8B7D6B', fontSize: 13 }}>No data</div>
          )}
        </div>

        {/* Plan Distribution */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Plan Distribution</span>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {distribution && ['free', 'pro', 'enterprise'].map((plan) => {
              const count = distribution[plan as keyof typeof distribution] as number;
              const total = distribution.free + distribution.pro + distribution.enterprise;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={plan}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#1A1714', textTransform: 'capitalize' }}>{plan}</span>
                    <span style={{ fontSize: 14, color: '#8B7D6B' }}>{count.toLocaleString()}</span>
                  </div>
                  <div style={{ height: 6, backgroundColor: '#F0EBE4', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: plan === 'free' ? '#D4A574' : plan === 'pro' ? '#D46A3A' : '#8B5E3C', borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
            {distribution && (
              <div style={{ backgroundColor: '#F5F2EC', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#22883F', fontWeight: 500 }}>
                {distribution.conversionRate}% conversion rate
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={sectionTitleStyle}>Recent Activity</span>
          <span style={{ fontSize: 13, color: '#D46A3A', fontWeight: 500, cursor: 'pointer' }}>View all</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {activity?.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: i < activity.length - 1 ? '1px solid #F0EBE4' : 'none' }}>
              <ActivityIcon type={item.type} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>{item.title}</div>
                <div style={{ fontSize: 13, color: '#8B7D6B', marginTop: 2 }}>{item.subtitle}</div>
              </div>
              <span style={{ fontSize: 13, color: '#8B7D6B', whiteSpace: 'nowrap' }}>{timeAgo(item.timestamp)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Screen Dropoff Analysis */}
      <ScreenDropoffAnalysis />
    </div>
  );
}

function ActivityIcon({ type }: { type: string }) {
  const base = { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 } as const;
  switch (type) {
    case 'signup': return <div style={{ ...base, backgroundColor: '#F0E8DF', color: '#8B5E3C' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/></svg></div>;
    case 'payment': return <div style={{ ...base, backgroundColor: '#E8F5E9', color: '#22883F' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div>;
    case 'payment_failed': return <div style={{ ...base, backgroundColor: '#FDE8E8', color: '#D44A3A' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg></div>;
    case 'esign': return <div style={{ ...base, backgroundColor: '#E8E0F5', color: '#7C3AED' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></div>;
    case 'upgrade': return <div style={{ ...base, backgroundColor: '#FFF3E0', color: '#D46A3A' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></div>;
    default: return <div style={{ ...base, backgroundColor: '#F5F2EC', color: '#8B7D6B' }}>•</div>;
  }
}

const DROPOFF_DATA = [
  { screen: 'Landing', dropoff: 12, volume: 1.0 },
  { screen: 'Sign Up', dropoff: 38, volume: 0.7 },
  { screen: 'Onboarding', dropoff: 52, volume: 0.65 },
  { screen: '1st Chat', dropoff: 18, volume: 0.5 },
  { screen: 'File Upload', dropoff: 67, volume: 0.55 },
  { screen: 'E-Sign', dropoff: 58, volume: 0.5 },
  { screen: 'Share', dropoff: 34, volume: 0.45 },
  { screen: 'Pro Upgrade', dropoff: 73, volume: 0.5 },
  { screen: 'Drafting', dropoff: 15, volume: 0.5 },
];

function getBubbleColor(dropoff: number): string {
  if (dropoff > 50) return '#A04030';
  if (dropoff >= 25) return '#D4A574';
  return '#E8D5C0';
}

function ScreenDropoffAnalysis() {
  const [range, setRange] = useState('30D');
  const chartHeight = 240;
  const maxDropoff = 80;

  return (
    <div style={{ ...cardStyle, marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <span style={sectionTitleStyle}>Screen Dropoff Analysis</span>
          <div style={{ fontSize: 13, color: '#8B7D6B', marginTop: 4 }}>
            Bubble size = user volume · Color intensity = dropoff rate · Last 30 days
          </div>
        </div>
        <ToggleGroup options={['7D', '30D', '90D']} value={range} onChange={setRange} />
      </div>

      {/* Chart area */}
      <div style={{ position: 'relative', height: chartHeight, border: '1px solid #F0EBE4', borderRadius: 8, padding: '0 20px' }}>
        {/* Y-axis labels */}
        {[0, 20, 40, 60, 80].map((tick) => (
          <div key={tick} style={{ position: 'absolute', left: 0, bottom: `${(tick / maxDropoff) * 100}%`, width: '100%', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#8B7D6B', width: 30, textAlign: 'right', flexShrink: 0 }}>{tick}%</span>
            {tick > 0 && <div style={{ flex: 1, height: 1, backgroundColor: '#F0EBE4', marginLeft: 8 }} />}
          </div>
        ))}

        {/* Bubbles */}
        <div style={{ position: 'absolute', left: 40, right: 10, top: 0, bottom: 0 }}>
          {DROPOFF_DATA.map((d, i) => {
            const x = (i / (DROPOFF_DATA.length - 1)) * 100;
            const y = (d.dropoff / maxDropoff) * 100;
            const size = 40 + d.volume * 40;
            return (
              <div key={d.screen} style={{
                position: 'absolute',
                left: `${x}%`,
                bottom: `${y}%`,
                width: size,
                height: size,
                borderRadius: '50%',
                backgroundColor: getBubbleColor(d.dropoff),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'translate(-50%, 50%)',
                opacity: 0.85,
              }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: d.dropoff > 50 ? '#fff' : '#1A1714' }}>{d.dropoff}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 40px 0 40px' }}>
        {DROPOFF_DATA.map((d) => (
          <span key={d.screen} style={{ fontSize: 11, color: '#8B7D6B', textAlign: 'center', flex: 1 }}>{d.screen}</span>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 24 }}>
          <LegendItem color="#E8D5C0" label="Low dropoff (<25%)" />
          <LegendItem color="#D4A574" label="Medium (25–50%)" />
          <LegendItem color="#A04030" label="High dropoff (>50%)" />
        </div>
        <span style={{ fontSize: 12, color: '#8B7D6B' }}>Bubble size = user volume reaching screen</span>
      </div>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, opacity: 0.85 }} />
      <span style={{ fontSize: 12, color: '#8B7D6B' }}>{label}</span>
    </div>
  );
}
